/**
 * Contrato do módulo `check-validation` do backend (PRD 00): fila de avaliação
 * das evidências dos registros de presença lançados manualmente no painel
 * (`DoctorCheck.origin = WEB`).
 *
 * Os enums reusam as strings do backend de propósito (padrão `PostStationStatus`):
 * o `Record<Enum, ...>` das constants garante cobertura em compile-time e não há
 * mapa de tradução em runtime. Status desconhecido cai em `UNKNOWN_STATUS_CONFIG`.
 */
export enum CheckValidationStatus {
  /** Registro que não passa por validação (origem ≠ WEB). */
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  /** Legado: lançado antes da fila existir, nunca avaliado. */
  NOT_EVALUATED = 'NOT_EVALUATED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum CheckValidationEventType {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REVERSED = 'REVERSED',
  EDITED_DURING_REVIEW = 'EDITED_DURING_REVIEW'
}

export enum CheckRejectionReason {
  ILLEGIBLE_EVIDENCE = 'ILLEGIBLE_EVIDENCE',
  WRONG_STATION = 'WRONG_STATION',
  DIVERGENT_HOURS = 'DIVERGENT_HOURS',
  INCOMPLETE_EVIDENCE = 'INCOMPLETE_EVIDENCE',
  REDIGITIZE = 'REDIGITIZE',
  FRAUD_SUSPICION = 'FRAUD_SUSPICION',
  OTHER = 'OTHER'
}

export interface CheckValidationUserRef {
  id: number
  name: string | null
}

/** Item de `GET /check-validation/queue`. */
export interface CheckValidationQueueItem {
  checkId: number
  validationStatus: CheckValidationStatus
  /** Lock otimista: vai no body de approve/reject; 409 quando desatualizado. */
  validationVersion: number
  validationSubmittedAt: string | null
  /** Mais de `OVERDUE_DAYS` (5) dias desde o envio para análise. */
  isOverdue: boolean
  doctor: CheckValidationUserRef & {
    crm?: string | null
    crmUf?: string | null
  }
  station: {
    id: number
    /** Instantes ISO (a coluna do backend é timestamp). */
    startHour: string
    endHour: string
    /** Offset ISO do plantão (`-03:00`); ausente → fuso padrão de Brasília. */
    timezone?: string | null
  }
  schedule: { id: number; name: string }
  healthUnit: { id: number; name: string } | null
  /** Quem lançou o registro — segregação de funções: não pode avaliar o próprio. */
  createdBy: CheckValidationUserRef | null
  /** Horários digitados (ISO). Ainda não devolvidos pela fila. */
  check?: {
    checkin: string | null
    checkout: string | null
  }
  evidenceCount?: number
  /** Registro editado após o envio para análise (`EDITED_DURING_REVIEW`). */
  validationEditedSinceSubmit?: boolean
}

export interface CheckValidationQueueResponse {
  items: CheckValidationQueueItem[]
  total: number
  limit: number
  offset: number
}

export interface CheckValidationEvidenceRef {
  id: number
  name: string | null
  path: string | null
  eventType: string | null
}

export interface CheckValidationDetailResponse {
  checkId: number
  checkin: string | null
  checkout: string | null
  origin: string
  checkMethod: string | null
  validationStatus: CheckValidationStatus
  validationVersion: number
  validationSubmittedAt: string | null
  validationEditedSinceSubmit: boolean
  isOverdue: boolean
  doctor: CheckValidationUserRef & { crm: string | null; crmUf: string | null }
  station: {
    id: number
    date: string
    startHour: string
    endHour: string
    timezone: string
  }
  schedule: { id: number; name: string }
  healthUnit: { id: number; name: string } | null
  createdBy: CheckValidationUserRef | null
  evidences: CheckValidationEvidenceRef[]
  history: CheckValidationHistoryEvent[]
}

export interface CheckValidationHistoryEvent {
  id: number
  eventType: CheckValidationEventType
  previousStatus: CheckValidationStatus | null
  newStatus: CheckValidationStatus
  performedBy: CheckValidationUserRef
  rejectionReason: CheckRejectionReason | null
  observation: string | null
  createdAt: string
}

export interface CheckValidationHistoryResponse {
  checkId: number
  events: CheckValidationHistoryEvent[]
}

export interface BulkApproveResultItem {
  checkId: number
  success: boolean
  error?: string
}

export interface BulkApproveCheckValidationResponse {
  results: BulkApproveResultItem[]
}
